import React, { useRef } from 'react';

import { CompensableViewsAggregations } from '~api/model/dashboardAggregations';

import { formatNumber, message } from '~helper/_common';

import { useShowDetailsTile } from '~hooks/useShowDetailsTile';

import { LivestreamLabel } from '~components/molecules/LivestreamLabel';
import { ReplaysLabel } from '~components/molecules/ReplaysLabel';
import { DataTile } from '~components/organism/DataTile';
import { DetailsTile } from '~components/organism/DetailsTile';
import { ViewsDetailsTile } from '~components/organism/ViewsDetailsTile';
import { ComponentCommonProps } from '~components/types';

export type CompensableViewsDataTileProps = ComponentCommonProps & {
    loading: boolean;
    data: CompensableViewsAggregations['compensableViews'] | null;
    creatorId?: string | null;
    hasDetailsTile?: boolean;
};

export const CompensableViewsDataTile: React.FC<CompensableViewsDataTileProps> = ({
    className,
    data,
    creatorId,
    loading,
    hasDetailsTile,
    'data-test': dataTest = 'compensableViewsDataTile',
}) => {
    const dataTileRef = useRef(null);
    const { showDetailsTile, setShowDetailsTile } = useShowDetailsTile();

    return (
        <>
            { showDetailsTile && (
                <DetailsTile
                    dataTileRef={ dataTileRef }
                    onClose={ () => setShowDetailsTile(false) }
                    data-test={ `${dataTest}.details` }
                >
                    <ViewsDetailsTile
                        data={ data }
                        creatorId={ creatorId }
                        mode="compensableViews"
                        data-test={ `${dataTest}.details.compensableViews` } />
                </DetailsTile>
            ) }
            <DataTile
                className={ className }
                contentType="compensableViews"
                headline={ message('dataTile.compensableViews.headline') }
                headlineSecondary={ message('common.total') }
                mainDataValue={ message('common.views', formatNumber(data?.total || 0)) }
                headlineVariant="h4"
                subHeadlineVariant="h5"
                loading={ loading }
                data-test={ dataTest }
                ref={ dataTileRef }
                onExpandClick={ hasDetailsTile ? () => setShowDetailsTile(true) : undefined }
                chartValues={ data?.history?.map(historyEntry => ({ date: historyEntry.date, value: historyEntry.total })) }>
                <LivestreamLabel
                    loading={ loading }
                    data-test={ `${dataTest}.liveViews` }>
                    { message('common.views', formatNumber(data?.liveViews || 0)) }
                </LivestreamLabel>
                <ReplaysLabel
                    loading={ loading }
                    data-test={ `${dataTest}.replayViews` }>
                    { message('common.views', formatNumber(data?.replayViews || 0)) }
                </ReplaysLabel>
            </DataTile>
        </>
    );
};
